// Writing colour into a terminal figure, without writing escape bytes by hand.
//
// The figures on this page are transcriptions: real lines from a real session,
// wrapped and elided to fit a panel (see `content.ts`). Colour is part of what
// those lines look like — tsh marks a turn, dims a separator and tints a rule,
// and a transcript reproduced in one flat grey has a component of the original
// dropped.
//
// It is written as ROLES rather than as colours. A role says what a run of text
// IS — a marker, a node reference, a piece of meta — and leaves what it looks
// like to the page's terminal palette in `tokens.ts`. That is the split a
// terminal already has: a program emits SGR names, and the terminal's theme
// decides what those names paint. Authoring `accent("●")` rather than a literal
// escape sequence keeps that decision in one file, and keeps the sample legible
// in a diff — a raw ESC byte is invisible in every review tool that will ever
// read it.
//
// Two roles deliberately carry no colour at all. `meta` and `rule` use SGR 2
// (dim), which the replay renders as opacity rather than as a darker ink — so
// they recede by the same proportion on any ground, and a later change to the
// panel's background cannot quietly turn every separator invisible. These are
// the roles a terminal uses most, and the ones most likely to fail a contrast
// check after an unrelated change.

/** Built rather than pasted: a literal ESC in a source file cannot be reviewed. */
const CSI = "\u001b[";

/** Wrap `text` in an SGR sequence and a full reset. */
function sgr(params: string, text: string): string {
  return `${CSI}${params}m${text}${CSI}0m`;
}

/**
 * A marker: the glyph that opens a turn, a bullet, a selection caret.
 *
 * Yellow because that is the SGR name the palette binds to the page's accent —
 * the one colour the tsh design already had.
 */
export const accent = (text: string): string => sgr("33", text);

/** A node reference: a session id, a semantic path, a turn ref, a datatype. */
export const node = (text: string): string => sgr("36", text);

/** Meta: separators, hints, timestamps — present, and not competing. */
export const meta = (text: string): string => sgr("2", text);

/** A drawn rule or frame. Same dimming as {@link meta}; named for the reader. */
export const rule = (text: string): string => sgr("2", text);

/** A column header. Weight rather than colour, which is what a table wants. */
export const header = (text: string): string => sgr("1", text);

/** Something the session added — a new column, an added line. */
export const added = (text: string): string => sgr("32", text);

/** Something the session removed. */
export const removed = (text: string): string => sgr("31", text);
